import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getExamAttemptPayloadForStudent } from '../services/exam2Api'
import { useResponsiveContext } from '../context/ResponsiveContext'

const cardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 12,
  background: '#ffffff',
}

function attemptStatusLabel(status) {
  if (!status) return 'غير معروف'
  switch (status) {
    case 'in_progress':
      return 'قيد التنفيذ'
    case 'submitted':
      return 'تم الإرسال'
    case 'graded':
      return 'تم التصحيح'
    case 'open':
      return 'مفتوح'
    case 'closed':
      return 'مغلق'
    case 'scheduled':
      return 'مقرر'
    default:
      return status
  }
}

function renderAnswerContent(q, ans) {
  const value = ans && ans.answer
  if (value == null) return 'لم يتم تسجيل إجابة.'

  const type = q?.type_key

  if (type === 'mcq') {
    const choices = Array.isArray(q?.payload?.choices) ? q.payload.choices : []
    const selectedIndex = typeof value?.selectedIndex === 'number' ? value.selectedIndex : null
    if (selectedIndex == null || !choices.length) return 'لم يتم اختيار أي خيار.'
    const label = choices[selectedIndex] || `خيار رقم ${selectedIndex + 1}`
    return label
  }

  if (type === 'true_false') {
    const statements = Array.isArray(q?.payload?.statements) ? q.payload.statements : []

    // New format: per-statement answers in value.statements
    if (statements.length) {
      const ansStatements = Array.isArray(value?.statements) ? value.statements : []
      if (!ansStatements.length) return 'لم يتم اختيار (صحيح / خطأ) لأي من العبارات.'

      return (
        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
          {statements.map((s, idx) => {
            const a = ansStatements[idx] || {}
            const v = typeof a.value === 'boolean' ? a.value : null
            const label =
              v == null
                ? 'لم يتم الاختيار'
                : v
                  ? 'صحيح'
                  : 'خطأ'
            return (
              <li key={idx} style={{ fontSize: 13, lineHeight: 1.7 }}>
                <div>
                  {idx + 1}
                  -
                  {' '}
                  {s?.text || '—'}
                </div>
                <div style={{ color: '#6b7280' }}>
                  الإجابة:
                  {' '}
                  {label}
                </div>
              </li>
            )
          })}
        </ul>
      )
    }

    // Legacy single-boolean format
    if (typeof value?.value !== 'boolean') return 'لم يتم اختيار (صحيح / خطأ).'
    return value.value ? 'صحيح' : 'خطأ'
  }

  if (type === 'matching') {
    const pairs = Array.isArray(value?.pairs) ? value.pairs : []
    const left = Array.isArray(q?.payload?.left) ? q.payload.left : []
    const right = Array.isArray(q?.payload?.right) ? q.payload.right : []
    if (!pairs.length) return 'لم يتم إكمال المزاوجة.'
    return (
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {pairs.map((p, idx) => {
          const lIdx = typeof p?.leftIndex === 'number' ? p.leftIndex : idx
          const rIdx = typeof p?.rightIndex === 'number' ? p.rightIndex : null
          const leftLabel = left[lIdx] || `العنصر ${lIdx + 1}`
          const rightLabel = rIdx == null ? '—' : right[rIdx] || `العنصر ${rIdx + 1}`
          return (
            <li key={`${lIdx}-${rIdx ?? 'x'}`} style={{ fontSize: 13 }}>
              {idx + 1}- {leftLabel} ↔ {rightLabel}
            </li>
          )
        })}
      </ul>
    )
  }

  if (type === 'fill_blank') {
    const lines = Array.isArray(value?.lines) ? value.lines : []
    if (!lines.length) return 'لم يتم إدخال إجابات في الفراغات.'
    return (
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {lines.map((l, idx) => (
          <li key={idx} style={{ fontSize: 13 }}>
            {idx + 1}- {l?.value || '—'}
          </li>
        ))}
      </ul>
    )
  }

  if (type === 'definition') {
    const items = Array.isArray(value?.items) ? value.items : []
    if (!items.length) return 'لم يتم إدخال تعريفات.'
    return (
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {items.map((it, idx) => (
          <li key={idx} style={{ fontSize: 13, lineHeight: 1.7 }}>
            {idx + 1}- {it?.text || '—'}
          </li>
        ))}
      </ul>
    )
  }

  if (type === 'explanation') {
    const parts = Array.isArray(value?.parts) ? value.parts : []
    if (!parts.length) return 'لم يتم إدخال تفسير.'
    return (
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {parts.map((p, idx) => (
          <li key={idx} style={{ fontSize: 13, lineHeight: 1.7 }}>
            {idx + 1}- {p?.text || '—'}
          </li>
        ))}
      </ul>
    )
  }

  if (type === 'figure_question') {
    if (!value || (typeof value !== 'string' && typeof value?.value !== 'string')) {
      return 'لم يتم إدخال إجابة.'
    }
    const text = typeof value === 'string' ? value : value.value
    return text || 'لم يتم إدخال إجابة.'
  }

  if (type === 'passage_group') {
    const passage = q?.payload?.passage || ''
    const embedded = Array.isArray(q?.payload?.questions) ? q.payload.questions : []
    const subAnswers = (value && value.subAnswers) || {}

    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {passage && (
          <div
            style={{
              padding: 8,
              background: '#f9fafb',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              fontSize: 13,
              lineHeight: 1.8,
            }}
          >
            {passage}
          </div>
        )}
        {embedded.length === 0 ? (
          <div style={{ fontSize: 13, color: '#6b7280' }}>لا توجد أسئلة فرعية معرفة لهذه القطعة.</div>
        ) : (
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {embedded.map((eq, idx) => {
              const inner = subAnswers[String(idx)]
              return (
                <li key={eq.id || idx} style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 6 }}>
                  <div style={{ fontWeight: 700 }}>
                    {idx + 1}.
                    {' '}
                    {eq.prompt || '(بدون نص)'}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {renderAnswerContent(eq, { answer: inner })}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }

  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? 'صحيح' : 'خطأ'

  if (Array.isArray(value)) {
    return (
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {value.map((item, idx) => (
          <li key={idx} style={{ fontSize: 12 }}>
            {typeof item === 'string' || typeof item === 'number'
              ? String(item)
              : 'إجابة مركّبة لا يوجد لها عرض تفصيلي.'}
          </li>
        ))}
      </ul>
    )
  }

  if (!Array.isArray(value) && typeof value === 'object' && typeof value.value === 'string') {
    return value.value
  }

  if (typeof value === 'object') {
    return 'تم تسجيل إجابة، لكن لا يوجد عرض نصي مخصص لها.'
  }

  return String(value)
}

export default function ExamAttemptReview() {
  const navigate = useNavigate()
  const { attemptId } = useParams()
  const { isMobile } = useResponsiveContext()

  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const [attempt, setAttempt] = useState(null)
  const [sessionRow, setSessionRow] = useState(null)
  const [paper, setPaper] = useState(null)
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState([])

  const answersByQuestionId = useMemo(() => {
    const map = new Map()
    for (const a of answers || []) {
      if (!a) continue
      map.set(String(a.question_id), a)
    }
    return map
  }, [answers])

  useEffect(() => {
    let cancelled = false
    async function run() {
      if (!attemptId) return
      setLoading(true)
      setErrorMsg('')
      try {
        const payload = await getExamAttemptPayloadForStudent(attemptId)
        if (!payload || cancelled) return
        setAttempt(payload.attempt || null)
        setSessionRow(payload.session || null)
        setPaper(payload.paper || null)
        setQuestions(Array.isArray(payload.questions) ? payload.questions : [])
        setAnswers(Array.isArray(payload.answers) ? payload.answers : [])
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e?.message || 'فشل تحميل بيانات المحاولة')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [attemptId])

  if (loading && !attempt) {
    return (
      <div dir="rtl" style={{ padding: 16 }}>
        جاري تحميل بيانات المحاولة...
      </div>
    )
  }

  if (errorMsg && !attempt) {
    return (
      <div dir="rtl" style={{ padding: 16 }}>
        <div style={{ padding: 10, borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#7f1d1d' }}>{errorMsg}</div>
      </div>
    )
  }

  if (!attempt || !paper) {
    return null
  }

  const title = paper.title || 'مراجعة محاولة امتحان أونلاين'

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '1.5rem',
      }}
    >
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
            display: 'grid',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{title}</h1>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                الحالة:
                {' '}
                {attemptStatusLabel(attempt.status)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => navigate('/exams')}
                style={{
                  padding: '8px 12px',
                  borderRadius: 999,
                  border: '1px solid #e5e7eb',
                  background: '#ffffff',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                العودة لقائمة الامتحانات
              </button>
            </div>
          </div>

          {errorMsg ? (
            <div style={{ padding: 10, borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#7f1d1d' }}>{errorMsg}</div>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,3fr) minmax(0,1.3fr)', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...cardStyle, display: 'grid', gap: 10 }}>
          <div style={{ fontWeight: 700 }}>الأسئلة وإجاباتك</div>
          {questions.length === 0 ? (
            <div style={{ padding: 8, color: '#6b7280' }}>لا توجد أسئلة مرتبطة بهذه الورقة.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {questions.map((q, idx) => {
                const qid = String(q.id)
                const ans = answersByQuestionId.get(qid)
                const isSection = q.type_key === 'section'
                return (
                  <div
                    key={qid}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 10,
                      background: isSection ? '#f9fafb' : '#ffffff',
                      display: 'grid',
                      gap: 6,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700 }}>
                        {idx + 1}.
                        {' '}
                        {q.prompt || '(بدون نص)'}
                      </div>
                      {!isSection && (
                        <div style={{ fontSize: 13, color: '#4b5563' }}>
                          الدرجة الكلية:
                          {' '}
                          {q.points != null ? q.points : '—'}
                        </div>
                      )}
                    </div>

                    {isSection ? (
                      <div style={{ fontSize: 13, color: '#4b5563' }}>
                        هذا صف تعليمي (Section) ولا يحتوي على إجابة.
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: '#111827' }}>
                        <span style={{ display: 'block', marginBottom: 4, color: '#6b7280' }}>إجابتك:</span>
                        <div style={{ padding: 8, background: '#f9fafb', borderRadius: 8, maxHeight: 220, overflow: 'auto', direction: 'rtl', textAlign: 'right' }}>
                          {renderAnswerContent(q, ans)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
            </div>

            <div style={{ ...cardStyle, display: 'grid', gap: 6, fontSize: 13 }}>
              <div style={{ fontWeight: 700 }}>ملخص المحاولة</div>
              <div>
                الحالة:
                {' '}
                {attemptStatusLabel(attempt.status)}
              </div>
              <div>
                بدأت في:
                {' '}
                {attempt.started_at ? new Date(attempt.started_at).toLocaleString('ar-EG') : '—'}
              </div>
              <div>
                أُرسلت في:
                {' '}
                {attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString('ar-EG') : '—'}
              </div>
              <div>
                تم التقييم في:
                {' '}
                {attempt.graded_at ? new Date(attempt.graded_at).toLocaleString('ar-EG') : '—'}
              </div>
              <div>
                الدرجة الحالية:
                {' '}
                {attempt.score != null ? attempt.score : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
